import React, { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import BackHomeButton from '../components/BackHomeButton';
import { auth, db, functions } from '../firebase';

const ADMIN_EMAIL = 'eascode.ltd@gmail.com';

const REVIEW_SOURCES = [
  {
    kind: 'lesson',
    label: 'Lesson',
    reviewCollection: 'Saved_Lessons_Review',
    targetCollection: 'Saved_lessons'
  },
  {
    kind: 'upload',
    label: 'Worksheet Upload',
    reviewCollection: 'uploads_Review',
    targetCollection: 'uploads'
  }
];

const REVIEW_ONLY_FIELDS = new Set([
  'reviewStatus',
  'reviewRequestedAt',
  'reviewedAt',
  'reviewedBy',
  'reviewedByUid',
  'publishedCollection',
  'publishedDocId',
  'rejectionReason',
  'emailStatus',
  'emailError'
]);

function cleanForPublish(data) {
  return Object.entries(data).reduce((acc, [key, value]) => {
    if (!REVIEW_ONLY_FIELDS.has(key) && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

function getSubmitterEmail(item) {
  return item.data.email || item.data.uploadedBy || item.data.userEmail || '';
}

function getSubmitterName(item) {
  return item.data.uploadername || item.data.displayName || item.data.name || getSubmitterEmail(item) || 'Eascode user';
}

function getTitle(item) {
  return (
    item.data.lessonName ||
    item.data.topicName ||
    item.data.calculatedFileName ||
    item.data.fileName ||
    `${item.label} submission`
  );
}

function getDateValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value.seconds) return value.seconds * 1000;
  return 0;
}

function formatDate(value) {
  const millis = getDateValue(value);
  return millis ? new Date(millis).toLocaleString() : 'Not available';
}

function ApprovedUserSubmissions() {
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingKey, setProcessingKey] = useState('');
  const [rejectReasons, setRejectReasons] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const isAdmin = currentUser?.email === ADMIN_EMAIL;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  const loadPendingReviews = async (clearAlerts = true) => {
    setLoading(true);
    if (clearAlerts) {
      setError('');
      setMessage('');
    }

    try {
      const pendingItems = [];

      for (const source of REVIEW_SOURCES) {
        const snapshot = await getDocs(collection(db, source.reviewCollection));

        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const status = data.reviewStatus || 'pending';

          if (status === 'pending') {
            pendingItems.push({
              ...source,
              id: docSnap.id,
              key: `${source.kind}:${docSnap.id}`,
              data
            });
          }
        });
      }

      pendingItems.sort((a, b) => {
        const bDate = getDateValue(b.data.reviewRequestedAt || b.data.uploadTime || b.data.timestamp);
        const aDate = getDateValue(a.data.reviewRequestedAt || a.data.uploadTime || a.data.timestamp);
        return bDate - aDate;
      });

      setItems(pendingItems);
    } catch (err) {
      console.error('Failed to load review submissions:', err);
      setError('Could not load pending submissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authChecked && isAdmin) {
      loadPendingReviews();
    }
  }, [authChecked, isAdmin]);

  const sendStatusEmail = async (item, status, reason = '') => {
    const submitterEmail = getSubmitterEmail(item);

    if (!submitterEmail) {
      throw new Error('Submitter email is missing.');
    }

    const sendEmail = httpsCallable(functions, 'sendWorksheetEmail');
    const title = getTitle(item);
    const approved = status === 'approved';

    await sendEmail({
      friendName: getSubmitterName(item),
      friendEmail: submitterEmail,
      worksheetTitle: `${item.label} submission ${approved ? 'approved' : 'not approved'}`,
      fileUrl: item.data.fileUrl || 'https://eascode.web.app/Old_Home',
      message: approved
        ? `Your ${item.label.toLowerCase()} submission "${title}" has been approved and published on Eascode.`
        : `Your ${item.label.toLowerCase()} submission "${title}" was not approved because it does not meet our standards. Reason: ${reason}`
    });
  };

  const updateReviewEmailStatus = async (item, emailStatus, emailError = '') => {
    await updateDoc(doc(db, item.reviewCollection, item.id), {
      emailStatus,
      ...(emailError && { emailError })
    });
  };

  const handleApprove = async (item) => {
    setProcessingKey(item.key);
    setError('');
    setMessage('');

    try {
      const finalData = cleanForPublish(item.data);
      const publishedRef = await addDoc(collection(db, item.targetCollection), {
        ...finalData,
        approvedAt: serverTimestamp(),
        approvedBy: currentUser.email,
        approvedByUid: currentUser.uid
      });

      await updateDoc(doc(db, item.reviewCollection, item.id), {
        reviewStatus: 'approved',
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.email,
        reviewedByUid: currentUser.uid,
        publishedCollection: item.targetCollection,
        publishedDocId: publishedRef.id
      });

      // --- NEW CONDITIONAL REWARD CAPTURE ---
      if (item.kind === 'lesson') {
        for (let i = 0; i < 5; i++) {
        await addDoc(collection(db, 'Reward_Capture'), {
          uid: item.data.uid || '',
          user_email: getSubmitterEmail(item),
          timestamp: serverTimestamp(),
          reward_category: 'Lesson_Approval'
        });
      }
      }

      try {
        await sendStatusEmail(item, 'approved');
        await updateReviewEmailStatus(item, 'sent');
      } catch (emailErr) {
        console.error('Approval email failed:', emailErr);
        await updateReviewEmailStatus(item, 'failed', emailErr.message || 'Email failed');
        setError('Submission was approved, but the notification email failed.');
      }

      setMessage(`${item.label} approved.`);



      await loadPendingReviews(false);
    } catch (err) {
      console.error('Approve failed:', err);
      setError(err.message || 'Could not approve this submission.');
    } finally {
      setProcessingKey('');
    }
  };

  const handleReject = async (item) => {
    const reason = (rejectReasons[item.key] || '').trim();

    if (!reason) {
      setError('Please write a rejection reason before rejecting.');
      return;
    }

    setProcessingKey(item.key);
    setError('');
    setMessage('');

    try {
      await updateDoc(doc(db, item.reviewCollection, item.id), {
        reviewStatus: 'rejected',
        rejectionReason: reason,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.email,
        reviewedByUid: currentUser.uid
      });

      try {
        await sendStatusEmail(item, 'rejected', reason);
        await updateReviewEmailStatus(item, 'sent');
      } catch (emailErr) {
        console.error('Rejection email failed:', emailErr);
        await updateReviewEmailStatus(item, 'failed', emailErr.message || 'Email failed');
        setError('Submission was rejected, but the notification email failed.');
      }

      setRejectReasons((prev) => ({ ...prev, [item.key]: '' }));
      setMessage(`${item.label} rejected.`);
      await loadPendingReviews(false);
    } catch (err) {
      console.error('Reject failed:', err);
      setError(err.message || 'Could not reject this submission.');
    } finally {
      setProcessingKey('');
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] px-4 py-8 text-center text-sm font-semibold text-gray-600">
        Loading...
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] px-4 py-8">
        <div className="mx-auto max-w-xl rounded-lg border border-gray-100 bg-white p-6 text-center shadow-sm">
          <div className="mb-5">
            <BackHomeButton />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900">Admin access required</h1>
          <p className="mt-3 text-sm font-medium text-gray-600">
            Only the Eascode admin account can review user submissions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA] px-4 py-6 font-sans">
      <div className="mx-auto max-w-5xl">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <BackHomeButton />
          <button
            type="button"
            onClick={() => loadPendingReviews()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="mb-5 rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
          <h1 className="text-2xl font-extrabold text-gray-900">Approved_User_Submissions</h1>
          <p className="mt-2 text-sm font-medium text-gray-600">
            Review pending lesson and worksheet uploads before publishing them to Eascode.
          </p>
        </div>

        {message && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
            {message}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-lg border border-gray-100 bg-white p-8 text-center text-sm font-semibold text-gray-500 shadow-sm">
            Loading pending submissions...
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-gray-100 bg-white p-8 text-center text-sm font-semibold text-gray-500 shadow-sm">
            No pending submissions to review.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => {
              const title = getTitle(item);
              const submitterEmail = getSubmitterEmail(item);
              const isProcessing = processingKey === item.key;

              return (
                <div key={item.key} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <span className="inline-flex rounded-md bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700">
                        {item.label}
                      </span>
                      <h2 className="mt-2 text-lg font-extrabold text-gray-900">{title}</h2>
                      <p className="mt-1 text-xs font-semibold text-gray-500">
                        From: {submitterEmail || 'Email missing'}
                      </p>
                    </div>
                    <p className="text-xs font-semibold text-gray-500">
                      Submitted: {formatDate(item.data.reviewRequestedAt || item.data.uploadTime || item.data.timestamp)}
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-gray-700 md:grid-cols-2">
                    <p><strong>Subject:</strong> {item.data.subject || 'Not available'}</p>
                    <p><strong>Standard:</strong> {item.data.standard || 'Not available'}</p>
                    <p><strong>School:</strong> {item.data.school || 'Not available'}</p>
                    <p><strong>City:</strong> {item.data.city || 'Not available'}</p>
                    <p><strong>Type:</strong> {item.data.type || item.label}</p>
                    <p><strong>Review collection:</strong> {item.reviewCollection}</p>
                    {item.data.fileUrl && (
                      <p className="md:col-span-2">
                        <strong>File:</strong>{' '}
                        <a
                          href={item.data.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-bold text-blue-600 hover:underline"
                        >
                          Open upload
                        </a>
                      </p>
                    )}
                    {item.data.text && (
                      <div className="md:col-span-2">
                        <strong>Lesson text:</strong>
                        <div className="mt-2 max-h-40 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed">
                          {item.data.text}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="mb-2 block text-xs font-bold uppercase text-gray-500">
                      Rejection reason
                    </label>
                    <textarea
                      value={rejectReasons[item.key] || ''}
                      onChange={(e) =>
                        setRejectReasons((prev) => ({ ...prev, [item.key]: e.target.value }))
                      }
                      rows={3}
                      placeholder="Write the reason before rejecting this submission."
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => handleApprove(item)}
                      disabled={Boolean(processingKey)}
                      className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {isProcessing ? 'Processing...' : 'Approve'}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleReject(item)}
                      disabled={Boolean(processingKey)}
                      className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ApprovedUserSubmissions;
